import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Box, Paper, IconButton, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ReportIcon from '@mui/icons-material/Report';

import { PageTemplate } from '../templates/PageTemplate';
import { SelectLineStation } from '../molecules/SelectLineStation';
import { CustomAxios as axios } from '../../common/axios';
import { CalcSettingModal } from '../organisms/CalcSettingModal';
import { ReportModal } from '../organisms/ReportModal';

import type { LineMaster, StationWithLine } from '../../types/station/station';
import type { CalcSettingType } from '../../types/calc/calc';
import { CalcBaseDateSetting } from '../../types/calc-base-date-setting/calc-base-date-setting';

export type SelectLineStationType = {
  station?: StationWithLine;
  line?: LineMaster;
};

export const Home = () => {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const debug = Boolean(query.get('debug'));

  const [selectedList, setSelectedList] = useState<SelectLineStationType[]>([
    { station: undefined, line: undefined },
  ]);
  const [dispSettingModal, setDispSettingModal] = useState(false);
  const [dispReportModal, setDispReportModal] = useState(false);

  const [calcResponse, setCalcResponse] = useState<any>();
  const [calcSetting, setCalcSetting] = useState<CalcSettingType>({
    originalRoute: false,
    departureStation: false,
    arrivalStation: false,
    otherLineRoundTrip: false,
    calcBaseDateId: undefined,
  });
  const [calcBaseDateData, setCalcBaseDateData] = useState<
    CalcBaseDateSetting[] | null
  >(null);
  useEffect(() => {
    axios
      .get(`/calc-base-date-setting`)
      .then((response) => {
        setCalcBaseDateData(response.data);

        // 現行運賃をデフォルトとして指定
        const current = (response.data as CalcBaseDateSetting[]).find(
          (item) => item.isCurrentFare
        );
        setCalcSetting((prev) => ({
          ...prev,
          calcBaseDateId: Number(current?.calcBaseDateSettingId),
        }));
      })
      .catch((err) => {
        console.warn(err);
      });
  }, []);

  const calcFare = async () => {
    const response = await axios.post('/fare/calc', {
      selected: selectedList.filter(
        (item) => item != null && (item?.line || item?.station)
      ),
      options: calcSetting,
    });
    setCalcResponse(response.data);
    console.log(selectedList);
  };

  // 計算可能な場合true
  const checkBeforeCalc = () => {
    const list = selectedList.filter(
      (item) => item != null && (item?.line || item?.station)
    );
    if (list.length) {
      return list[list.length - 1]?.station && !list[list.length - 1]?.line;
    }
    return false;
  };

  /** 計算設定を開く */
  const openSettingModal = () => {
    setDispSettingModal(true);
  };

  return (
    <PageTemplate headerText="">
      {/* 経由駅 */}
      <Paper
        variant="outlined"
        sx={{
          padding: '20px 10px',
        }}
      >
        <Typography variant="h6" gutterBottom>
          経路入力
        </Typography>

        <SelectLineStation
          selectedList={selectedList}
          setSelectedList={setSelectedList}
          debug={debug}
        />

        {selectedList.length >= 2 && (
          <Box
            sx={{
              mt: '12px',
              display: 'flex',
              width: '350px',
              justifyContent: 'justify-around',
            }}
          >
            <div style={{ width: '50px' }} />
            <Button
              variant="contained"
              sx={{ width: '100px' }}
              onClick={calcFare}
              disabled={!checkBeforeCalc()}
            >
              計算
            </Button>
            <IconButton
              color="primary"
              aria-label="add to shopping cart"
              sx={{ width: '50px' }}
              onClick={openSettingModal}
            >
              <SettingsIcon />
            </IconButton>
          </Box>
        )}

        {/* 計算結果 */}
        {calcResponse && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              width: '350px',
              border: '1px solid gray',
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1">
              <strong>計算結果:</strong>
            </Typography>
            <span>
              <div>{`営業キロ: ${
                calcResponse?.distance.toLocaleString() ?? ''
              }`}</div>
              <div>{`有効期間: ${calcResponse?.validity ?? ''}日`}</div>
              <div>{`運賃: ${
                calcResponse?.fare.toLocaleString() ?? ''
              }円`}</div>
              {/* 特定都区市内 */}
              {(calcResponse?.rule?.specificMetropolitanArea
                ?.departureAreaName ||
                calcResponse?.rule?.specificMetropolitanArea
                  ?.arrivalAreaName) && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    width: '310px',
                    border: '1px solid gray',
                    borderRadius: 2,
                  }}
                >
                  <div>特定都区市内制度：適用あり</div>
                  {calcResponse?.rule?.specificMetropolitanArea
                    ?.departureAreaName && (
                    <div>{`発駅を${calcResponse?.rule?.specificMetropolitanArea?.departureAreaName}として計算しました。`}</div>
                  )}
                  {calcResponse?.rule?.specificMetropolitanArea
                    ?.arrivalAreaName && (
                    <div>{`着駅を${calcResponse?.rule?.specificMetropolitanArea?.arrivalAreaName}として計算しました。`}</div>
                  )}
                </Box>
              )}
              {/* 大都市近郊区間内 */}
              {calcResponse?.rule?.metropolitanAreaName && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    width: '310px',
                    border: '1px solid gray',
                    borderRadius: 2,
                  }}
                >
                  <div>大都市近郊区間内の特例：適用あり</div>
                  <p>{`${calcResponse?.rule?.metropolitanAreaName}近郊区間内で完結する経路です。`}</p>
                  <p>
                    計算結果より短い（運賃が安くなる）経路が存在する可能性があります。
                  </p>
                </Box>
              )}
            </span>

            <Button
              variant="outlined"
              startIcon={<ReportIcon />}
              sx={{ marginTop: '20px' }}
              onClick={() => {
                setDispReportModal(true);
              }}
            >
              計算結果の誤りを報告する
            </Button>
          </Box>
        )}

        {/* 選択した駅のリストを表示 */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            width: '350px',
            border: '1px solid gray',
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle1">
            <strong>選択した駅リスト:</strong>
          </Typography>
          <ul>
            {selectedList
              .filter(({ station }) => station)
              .map(({ station }) => (
                <li>{station!.stationName}</li>
              ))}
          </ul>
        </Box>
      </Paper>
      {dispSettingModal && (
        <CalcSettingModal
          dispSettingModal
          setDispSettingModal={setDispSettingModal}
          calcSetting={calcSetting}
          setCalcSetting={setCalcSetting}
          calcBaseDateData={calcBaseDateData}
        />
      )}
      {dispReportModal && (
        <ReportModal
          dispReportModal
          setDispReportModal={setDispReportModal}
          calcSetting={calcSetting}
          route={selectedList}
          result={JSON.stringify(calcResponse)}
        />
      )}
    </PageTemplate>
  );
};
